import React, { useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { resolveActiveSearcherId, visibleSearchers } from 'shared/searchers';
import { HeaderTemplateProps } from './types';
import { resolveHeaderConfig, resolveSocialLinks, resolveSearcherItems } from './resolveConfig';
import { CONTACT_ICONS, SOCIAL_ICONS, MenuIcon } from './icons';
import { useDragScroll } from '../useDragScroll';
import { MenuDrawer } from 'frontend/core/components/MenuDrawer';
import ReserveSearchPanel from './ReserveSearchPanel';
import EcommerceMenuRow from './EcommerceMenuRow';
import { manifest } from './manifest';
import './Header.css';
import './HeaderTemplates.css';
import './EcommerceSlider.css';

const PhoneIcon = CONTACT_ICONS.phone;
const EmailIcon = CONTACT_ICONS.email;

function SearcherNav({
  searchers,
  activeSearcherId,
  onSearcherSelect,
  drag,
  className = 'header-topbar__searcher-nav',
}: {
  searchers: ReturnType<typeof visibleSearchers>;
  activeSearcherId: string;
  onSearcherSelect?: (searcherId: string) => void;
  drag: ReturnType<typeof useDragScroll<HTMLElement>>;
  className?: string;
}) {
  if (searchers.length === 0) {
    return null;
  }

  return (
    <nav
      className={className}
      onMouseDown={drag.onMouseDown}
      onMouseLeave={drag.onMouseLeave}
      onMouseMove={drag.onMouseMove}
      onMouseUp={drag.onMouseUp}
      ref={drag.ref}
    >
      {searchers.map((searcher) => {
        const iconStyle = {
          '--searcher-icon-url': `url("${searcher.iconUrl}")`,
        } as React.CSSProperties;

        if (searcher.urlOnly) {
          return (
            <a
              className='header-topbar__searcher-item header-topbar__searcher-item--link'
              href={searcher.url || '#'}
              key={searcher.id}
              onClick={(e) => {
                if (drag.consumeDragged() || !searcher.url) e.preventDefault();
              }}
              rel='noopener noreferrer'
              target='_blank'
            >
              <span className='header-topbar__searcher-icon' style={iconStyle} />
              <span>{searcher.label}</span>
            </a>
          );
        }

        const isActive = searcher.id === activeSearcherId;
        return (
          <button
            className={[
              'header-topbar__searcher-item',
              isActive && 'header-topbar__searcher-item--active',
            ]
              .filter(Boolean)
              .join(' ')}
            key={searcher.id}
            onClick={() => {
              if (drag.consumeDragged()) return;
              onSearcherSelect?.(searcher.id);
            }}
            type='button'
          >
            <span className='header-topbar__searcher-icon' style={iconStyle} />
            <span>{searcher.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

const SearchIcon: React.FC = () => (
  <svg aria-hidden='true' fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
    <circle cx='11' cy='11' r='7' />
    <line x1='21' x2='16.65' y1='21' y2='16.65' />
  </svg>
);

const Header: React.FC<HeaderTemplateProps> = ({
  config: partialConfig,
  variant,
  onSearcherSelect,
  menuPortalContainer,
  heroSearchConfig,
  renderSearchForm,
  mobileSearchOpen = false,
  onMobileSearchToggle,
  paisId,
}) => {
  const config = resolveHeaderConfig(manifest, partialConfig);
  const socialLinks = resolveSocialLinks(manifest, config);
  const searcherItems = resolveSearcherItems(manifest, config);
  const searchers = visibleSearchers(searcherItems);
  const activeSearcherId = resolveActiveSearcherId(searcherItems, config.activeSearcherId);
  const drag = useDragScroll<HTMLElement>();
  const [reservePanelOpen, setReservePanelOpen] = useState(false);
  const [ecommercePanelOpen, setEcommercePanelOpen] = useState(false);
  const [menuDrawerOpen, setMenuDrawerOpen] = useState(false);
  const isPhone = useMediaQuery('(max-width: 640px)');

  const isHeroA = variant === 'hero_a';
  const isReserve = variant === 'reserve';
  const isEcommerce = variant === 'ecommerce';
  const isSlider = variant === 'ecommerce_slider';
  const showTopbarContactDetails = isHeroA || isEcommerce || isSlider;

  const hasBmSearchers = searchers.some((searcher) => !searcher.urlOnly);
  const showReserveToggle = isReserve && hasBmSearchers;

  const rootClassName = [
    'header-topbar',
    `header-topbar--${variant}`,
    config.fullBleed && isHeroA && 'header-topbar--full-bleed',
    isReserve && !showReserveToggle && 'header-topbar--no-reserve-toggle',
  ]
    .filter(Boolean)
    .join(' ');

  const rootStyle = {
    '--header-contact-color': config.contactColor,
    '--header-brand-color': config.brandColor,
    // Menu button ("Abrir menú") uses primary color on Default; reserve/ecommerce override in CSS.
    '--header-icon-color': config.brandColor,
    '--brand': config.brandColor,
    '--reserve-button-color': config.brandColor2 || '#152956',
    '--icon-color': config.iconColor,
    '--contact-color': config.contactColor,
    '--contact-text-color': config.contactTextColor || '#1c2836',
  } as React.CSSProperties;

  // Ecommerce: each searcher tab toggles the collapsible panel (prototipo v18).
  // Clicking the already-active tab while open closes it; otherwise opens/switches.
  function handleEcommerceTabSelect(searcherId: string) {
    if (ecommercePanelOpen && searcherId === activeSearcherId) {
      setEcommercePanelOpen(false);
      return;
    }
    onSearcherSelect?.(searcherId);
    setEcommercePanelOpen(true);
  }

  // Default + Institucional: topbar has no SearcherNav; mobile uses lupa → hero search panel.
  // Institucional desktop uses "Reservá Online" → ReserveSearchPanel instead.
  const showTopbarSearcherNav = false;
  const showMobileSearchToggle =
    Boolean(onMobileSearchToggle) &&
    (((isHeroA || isReserve) && hasBmSearchers) || (isSlider && searchers.length > 0));

  function scrollSearchersNext() {
    const nav = drag.ref.current;
    if (!nav) return;
    const amount = Math.max(180, nav.clientWidth * 0.65);
    const atEnd = nav.scrollLeft + nav.clientWidth >= nav.scrollWidth - 8;
    nav.scrollTo({ left: atEnd ? 0 : nav.scrollLeft + amount, behavior: 'smooth' });
  }

  return (
    <div className={['header-chrome', `header-chrome--${variant}`].join(' ')} style={rootStyle}>
      <header className={rootClassName}>
        <a className='header-topbar__logo' href='/'>
          {config.logoImageUrl ? (
            <img alt={config.logoText || 'Logo'} src={config.logoImageUrl} />
          ) : (
            <span className='header-topbar__logo-text'>{config.logoText}</span>
          )}
        </a>

        {showTopbarSearcherNav ? (
          <SearcherNav
            activeSearcherId={activeSearcherId}
            drag={drag}
            onSearcherSelect={onSearcherSelect}
            searchers={searchers}
          />
        ) : null}

        {showTopbarSearcherNav && searchers.length > 1 ? (
          <button
            aria-label='Ver más buscadores'
            className='header-topbar__searcher-next'
            onClick={scrollSearchersNext}
            type='button'
          >
            <svg fill='none' height='14' viewBox='0 0 8 14' width='8'>
              <path
                d='M1 1l6 6-6 6'
                stroke='currentColor'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
              />
            </svg>
          </button>
        ) : null}

        {showReserveToggle ? (
          <button
            aria-controls='header-reserve-search-panel'
            aria-expanded={reservePanelOpen}
            className={[
              'header-reserve-toggle',
              reservePanelOpen && 'header-reserve-toggle--active',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setReservePanelOpen((open) => !open)}
            type='button'
          >
            <span aria-hidden='true' className='header-reserve-toggle__icon'>
              <SearchIcon />
            </span>
            <span>Reservá Online</span>
          </button>
        ) : null}

        <div className='header-topbar__contact'>
          {showTopbarContactDetails && config.showPhone && config.phone ? (
            <a
              aria-label={config.phone}
              className='header-topbar__contact-item'
              href={`tel:${config.phone.replace(/[^+\d]/g, '')}`}
            >
              <span className='header-topbar__contact-icon'>
                <PhoneIcon />
              </span>
              <span className='header-topbar__contact-text'>{config.phone}</span>
            </a>
          ) : null}
          {showTopbarContactDetails && config.showEmail && config.email ? (
            <a
              aria-label={config.email}
              className='header-topbar__contact-item'
              href={`mailto:${config.email}`}
            >
              <span className='header-topbar__contact-icon'>
                <EmailIcon />
              </span>
              <span className='header-topbar__contact-text'>{config.email}</span>
            </a>
          ) : null}
          {showTopbarContactDetails &&
            socialLinks
              .filter((social) => social.show)
              .map((social, index) => {
                const Icon = SOCIAL_ICONS[social.network];
                return (
                  <a
                    className='header-topbar__social-icon'
                    href={social.url || '#'}
                    key={`${social.network}-${index}`}
                    rel='noreferrer'
                    target='_blank'
                    title={social.network}
                  >
                    <Icon />
                  </a>
                );
              })}
          <div className='header-topbar__mobile-actions'>
            {showMobileSearchToggle ? (
              <button
                aria-controls='hero-search-module'
                aria-expanded={mobileSearchOpen}
                aria-label='Buscar'
                className={[
                  'header-topbar__mobile-search-toggle',
                  mobileSearchOpen && 'header-topbar__mobile-search-toggle--active',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={onMobileSearchToggle}
                type='button'
              >
                <SearchIcon />
              </button>
            ) : null}
            <MenuDrawer
              iconColor={config.brandColor || '#225e8f'}
              items={config.menuItems}
              onOpenChange={setMenuDrawerOpen}
              open={menuDrawerOpen}
              portalContainer={menuPortalContainer}
              sidePanel={isSlider && !isPhone}
              triggerClassName='header-topbar__menu-button'
              triggerIcon={<MenuIcon />}
            />
          </div>
        </div>
      </header>

      {isEcommerce && searchers.length > 0 ? (
        <div className='header-ecommerce-search-row'>
          <div className='header-ecommerce-search-row__inner'>
            <nav className='header-ecommerce-tabs'>
              {searchers.map((searcher) => {
                const iconStyle = {
                  '--searcher-icon-url': `url("${searcher.iconUrl}")`,
                } as React.CSSProperties;
                if (searcher.urlOnly) {
                  return (
                    <a
                      className='header-ecommerce-tabs__link'
                      href={searcher.url || '#'}
                      key={searcher.id}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      <span className='header-ecommerce-tabs__icon' style={iconStyle} />
                      <span>{searcher.label}</span>
                    </a>
                  );
                }
                const isActive = searcher.id === activeSearcherId;
                return (
                  <button
                    aria-expanded={ecommercePanelOpen && isActive}
                    className={[
                      'header-ecommerce-tabs__btn',
                      isActive && 'header-ecommerce-tabs__btn--active',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    key={searcher.id}
                    onClick={() => handleEcommerceTabSelect(searcher.id)}
                    type='button'
                  >
                    <span className='header-ecommerce-tabs__icon' style={iconStyle} />
                    <span>{searcher.label}</span>
                  </button>
                );
              })}
            </nav>
            {/* X only while the collapsible search panel is open (prototipo v18). */}
            <button
              aria-label='Cerrar buscador'
              className='header-reserve-panel__close'
              hidden={!ecommercePanelOpen}
              onClick={() => setEcommercePanelOpen(false)}
              type='button'
            >
              ×
            </button>
          </div>
        </div>
      ) : null}

      {/* Panel between searcher tabs and sections menu (prototipo ecommerce order). */}
      {isEcommerce ? (
        <ReserveSearchPanel
          headerConfig={config}
          heroSearchConfig={heroSearchConfig}
          onClose={() => setEcommercePanelOpen(false)}
          onSearcherSelect={onSearcherSelect}
          open={ecommercePanelOpen}
          paisId={paisId}
          renderSearchForm={renderSearchForm}
          showPanelTopRow={false}
          variant='ecommerce'
        />
      ) : null}

      {(isEcommerce || isSlider) && config.menuItems.length > 0 ? (
        <EcommerceMenuRow
          itemGap={isSlider ? 8 : 0}
          items={config.menuItems}
          maxVisible={5}
          moreIcon={isSlider ? 'megaphone' : 'grid'}
          onMoreClick={() => setMenuDrawerOpen(true)}
        />
      ) : null}

      {isReserve ? (
        <ReserveSearchPanel
          headerConfig={config}
          heroSearchConfig={heroSearchConfig}
          onClose={() => setReservePanelOpen(false)}
          onSearcherSelect={onSearcherSelect}
          open={reservePanelOpen}
          paisId={paisId}
          renderSearchForm={renderSearchForm}
          showPanelTopRow
          variant='reserve'
        />
      ) : null}
    </div>
  );
};

export default Header;
